import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Target, 
  Eye, 
  Heart, 
  Award, 
  Users, 
  Globe, 
  Lightbulb,
  ArrowRight,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const values = [
  {
    icon: Target,
    title: "Results-Driven",
    description: "We focus on measurable outcomes that directly impact your business growth and success."
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "Staying ahead of digital trends to bring cutting-edge solutions to our clients."
  },
  {
    icon: Heart,
    title: "Client-Centric",
    description: "Your success is our success. We build lasting partnerships based on trust and transparency."
  },
  {
    icon: Award,
    title: "Excellence",
    description: "Committed to delivering the highest quality work in everything we do."
  }
];

const milestones = [
  { year: "2025", title: "Founded", description: "Omnilink Solutions was established with a vision to transform digital marketing." },
  { year: "Q2 2025", title: "Team Expansion", description: "Grew our team with specialists in SEO, Social Media, and PPC." },
  { year: "Q3 2025", title: "International Reach", description: "Started serving international clients across multiple continents." },
  { year: "Q4 2025", title: "Full-Service Agency", description: "Became a comprehensive full-service digital marketing agency." }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const About = () => {
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#4f46e5] font-medium text-sm uppercase tracking-wider">About Us</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mt-2 mb-6 leading-tight">
                Your Partner in Digital Growth
              </h1>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Omnilink Solutions is a full-service digital marketing agency dedicated to 
                connecting brands with their audiences through innovative, data-driven strategies. 
                We combine creativity with analytics to deliver campaigns that make an impact.
              </p>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Since our founding, we've helped businesses of all sizes—from startups to 
                enterprises—navigate the digital landscape and achieve sustainable growth. 
                Our team of experts brings together diverse skills in SEO, social media, 
                paid advertising, content creation, and web development.
              </p>
              <div className="flex gap-4">
                <Link to="/services">
                  <Button 
                    data-testid="about-services-btn"
                    className="bg-[#1e3a8a] hover:bg-[#172554] text-white px-6"
                  >
                    Our Services
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    data-testid="about-contact-btn"
                    variant="outline" 
                    className="border-slate-300 hover:bg-slate-100 px-6"
                  >
                    Get in Touch
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                alt="Omnilink Team"
                className="rounded-2xl shadow-xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-6 border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#4f46e5]/10 rounded-lg flex items-center justify-center">
                    <Globe className="w-6 h-6 text-[#4f46e5]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Global Presence</p>
                    <p className="text-2xl font-bold text-slate-900">15+ Countries</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm"
            >
              <div className="w-14 h-14 bg-[#1e3a8a] rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Mission</h2>
              <p className="text-slate-600 leading-relaxed">
                To empower businesses with innovative digital marketing solutions that drive 
                measurable growth. We strive to be the bridge between brands and their 
                audiences, creating meaningful connections through data-driven strategies 
                and creative excellence.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm"
            >
              <div className="w-14 h-14 bg-[#4f46e5] rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Vision</h2>
              <p className="text-slate-600 leading-relaxed">
                To be the most trusted digital marketing partner for businesses worldwide, 
                known for our commitment to innovation, transparency, and delivering 
                exceptional results. We envision a future where every brand can thrive 
                in the digital ecosystem.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-[#4f46e5] font-medium text-sm uppercase tracking-wider">Our Values</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-4">
              What Drives Us Forward
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our core values shape everything we do—from how we work with clients 
              to how we develop strategies and measure success.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {values.map((value) => (
              <motion.div
                key={value.title}
                variants={itemVariants}
                className="bg-white rounded-xl p-6 border border-slate-200 card-hover"
              >
                <div className="w-12 h-12 bg-[#4f46e5]/10 rounded-lg flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-[#4f46e5]" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{value.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-[#4f46e5] font-medium text-sm uppercase tracking-wider">Our Journey</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-2 mb-4">
              Building Excellence Since 2025
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-slate-200 hidden lg:block"></div>

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative flex flex-col lg:flex-row items-center gap-8 ${
                    index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                  }`}
                >
                  <div className={`lg:w-1/2 ${index % 2 === 0 ? "lg:text-right lg:pr-12" : "lg:text-left lg:pl-12"}`}>
                    <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm inline-block">
                      <span className="text-[#4f46e5] font-bold text-2xl">{milestone.year}</span>
                      <h3 className="text-lg font-semibold text-slate-900 mt-2">{milestone.title}</h3>
                      <p className="text-slate-600 text-sm mt-1">{milestone.description}</p>
                    </div>
                  </div>
                  
                  {/* Timeline dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-[#4f46e5] rounded-full border-4 border-white shadow hidden lg:block"></div>
                  
                  <div className="lg:w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#1e3a8a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-blue-200 font-medium text-sm uppercase tracking-wider">Why Omnilink</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mt-2 mb-6">
                Your Growth, Our Priority
              </h2>
              <p className="text-blue-100 mb-8 leading-relaxed">
                We're not just another digital marketing agency. We're your strategic partner 
                committed to understanding your business and delivering tailored solutions 
                that drive real results.
              </p>
              
              <ul className="space-y-4">
                {[
                  "Customized strategies for your unique business goals",
                  "Transparent reporting and regular communication",
                  "Experienced team with diverse industry expertise",
                  "Proven track record with domestic and international clients",
                  "Continuous optimization based on data insights"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#4f46e5] flex-shrink-0 mt-0.5" />
                    <span className="text-blue-100">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="grid grid-cols-2 gap-6"
            >
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <Globe className="w-8 h-8 text-white mb-3" />
                <h3 className="text-white font-semibold mb-2">Global Reach</h3>
                <p className="text-blue-200 text-sm">Serving clients domestically and internationally</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <Target className="w-8 h-8 text-white mb-3" />
                <h3 className="text-white font-semibold mb-2">Data-Driven</h3>
                <p className="text-blue-200 text-sm">Every decision backed by analytics</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <Award className="w-8 h-8 text-white mb-3" />
                <h3 className="text-white font-semibold mb-2">Excellence</h3>
                <p className="text-blue-200 text-sm">Committed to delivering quality results</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                <Users className="w-8 h-8 text-white mb-3" />
                <h3 className="text-white font-semibold mb-2">Dedicated Team</h3>
                <p className="text-blue-200 text-sm">Expert professionals at your service</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Ready to Start Your Digital Journey?
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto mb-8">
              Let's discuss how we can help your business grow. Our team is ready 
              to create a customized strategy for your success.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact">
                <Button 
                  data-testid="about-cta-contact-btn"
                  size="lg" 
                  className="bg-[#1e3a8a] hover:bg-[#172554] text-white px-8"
                >
                  Contact Us Today
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/careers">
                <Button 
                  data-testid="about-cta-careers-btn"
                  variant="outline" 
                  size="lg" 
                  className="border-slate-300 hover:bg-slate-50 px-8"
                >
                  Join Our Team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
